import { useEffect, useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { ResourceType } from '@cotera/api';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import {
  queryKeys,
  RESOURCE_LIST_FETCH_STALE_MS,
} from '@cotera/client/app/hooks/query-cache-keys';
import {
  buildNestedWorkflowTree,
  listNestedWorkflowIdsToFetch,
  summarizeNestedWorkflowTree,
  type NestedWorkflowSource,
  type NestedWorkflowTree,
  type NestedWorkflowTreeSummary,
} from './nested-workflow-tree';
import { readWorkflowResource } from './workflow-resource-read';
import type { WorkflowGraphApiData } from './workflow-graph.types';

/**
 * A signature of the child-graph queries, for memoising off `useQueries`.
 *
 * `dataUpdatedAt` is the load-bearing half. Keying on `status` alone looks
 * sufficient and is not: React Query holds `status` at `'success'` right through
 * a background refetch, including the invalidation after an edit saves a nested
 * workflow. So a child whose graph had just changed produced an identical key,
 * the memo never recomputed, and the tree kept drawing the pre-edit graph until
 * it remounted. `dataUpdatedAt` moves on every write, which is the signal that a
 * refetch actually landed.
 *
 * Same-data refetches are still cheap: they bump the signature, but React Query's
 * structural sharing keeps the graph reference identical, so the fold below sees
 * no change and leaves state alone.
 */
export function nestedWorkflowResultsSignature(
  results: readonly {
    readonly status: string;
    readonly dataUpdatedAt: number;
  }[]
): string {
  return results
    .map((result) => `${result.status}:${result.dataUpdatedAt}`)
    .join('|');
}

export type NestedWorkflowTreeState = {
  readonly tree: NestedWorkflowTree | null;
  readonly summary: NestedWorkflowTreeSummary | null;
  /** True while any workflow in the tree is still being fetched. */
  readonly isLoading: boolean;
};

const EMPTY_SOURCES: ReadonlyMap<string, NestedWorkflowSource> = new Map();

/**
 * Resolves the whole nested-workflow tree under `workflowId`.
 *
 * The recursion is a fixpoint across renders rather than a loop: the frontier is
 * computed from the graphs resolved so far, `useQueries` fetches it, the results
 * fold back into `resolved`, and that reveals the next level. It settles once a
 * pass adds nothing new, which is exactly "no `run-workflow` nodes left".
 *
 * The root graph is passed in rather than fetched, so unsaved `run-workflow`
 * nodes on the canvas show up in the tree immediately.
 *
 * Reads through the same `queryKeys.resource.get` cache the rest of the builder
 * uses, so a child the node editor already fetched costs nothing here.
 */
export function useNestedWorkflowTree(params: {
  workflowId: string;
  name: string | null;
  graph: WorkflowGraphApiData | null;
}): NestedWorkflowTreeState {
  const { workflowId, name, graph } = params;
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();

  // Graphs resolved so far, keyed by workflow id. Accumulated (never pruned):
  // dropping an id the frontier stopped asking for would shrink the frontier,
  // which would re-add it on the next pass and never settle.
  const [resolved, setResolved] =
    useState<ReadonlyMap<string, NestedWorkflowSource>>(EMPTY_SOURCES);

  const idsToFetch = useMemo(() => {
    if (graph === null) {
      return [];
    }
    return listNestedWorkflowIdsToFetch({
      workflowId,
      graph,
      sources: resolved,
    });
  }, [graph, workflowId, resolved]);

  // No `select` on purpose: an inline `select` is a fresh function each render,
  // so React Query would re-run it and hand back a new object identity every
  // time, and the effect below would never stop firing. Unwrapping the (stable,
  // structurally shared) Result in a memo keeps the identity stable instead.
  const results = useQueries({
    queries: idsToFetch.map((id) => ({
      queryKey: queryKeys.resource.get({
        orgId,
        type: ResourceType.Workflow,
        id,
      }),
      staleTime: RESOURCE_LIST_FETCH_STALE_MS,
      queryFn: () => client.resource.get({ type: ResourceType.Workflow, id }),
    })),
  });

  const resultsSignature = nestedWorkflowResultsSignature(results);

  const settled = useMemo(() => {
    const entries: Array<readonly [string, NestedWorkflowSource]> = [];
    idsToFetch.forEach((id, index) => {
      const result = results[index];
      if (result === undefined || result.isPending) {
        return;
      }
      const workflow = readWorkflowResource(result.data);
      if (workflow === null) {
        entries.push([id, { status: 'missing' }]);
        return;
      }
      entries.push([
        id,
        { status: 'ready', name: workflow.name, graph: workflow.graph },
      ]);
    });
    return entries;
    // `results` is a fresh array every render, so this keys on a signature of it
    // instead. See `nestedWorkflowResultsSignature` for why status alone is not
    // enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsToFetch, resultsSignature]);

  useEffect(() => {
    if (settled.length === 0) {
      return;
    }
    setResolved((previous) => {
      const next = new Map(previous);
      let changed = false;
      for (const [id, source] of settled) {
        const existing = next.get(id);
        if (existing !== undefined && existing.status === source.status) {
          if (
            source.status === 'missing' ||
            (existing.status === 'ready' && existing.graph === source.graph)
          ) {
            continue;
          }
        }
        next.set(id, source);
        changed = true;
      }
      return changed ? next : previous;
    });
  }, [settled]);

  const tree = useMemo(() => {
    if (graph === null) {
      return null;
    }
    return buildNestedWorkflowTree({
      workflowId,
      name,
      graph,
      sources: resolved,
    });
  }, [graph, name, workflowId, resolved]);

  const summary = useMemo(
    () => (tree === null ? null : summarizeNestedWorkflowTree(tree)),
    [tree]
  );

  return {
    tree,
    summary,
    isLoading:
      results.some((result) => result.isPending) ||
      (summary !== null && summary.loadingCount > 0),
  };
}
